import React, { useCallback, useEffect, useRef, useState } from 'react';
import DataTable from 'datatables.net-dt';
import 'datatables.net-dt/css/dataTables.dataTables.css';
import { createPortal } from 'react-dom';
import client from '../../../api/client';
import { anchorRect, useAnchoredMenu } from '../useAnchoredMenu';
import { filterApprovedViewerRows } from '../../../api/viewerAccess';
import './nlims-datatable.css';

function escapeAttr(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function statusBadgeHtml(status) {
  if (!status) return '';
  const key = String(status).toLowerCase().replace(/\s+/g, '-');
  return `<span class="nlims-status nlims-status--${key}">${status}</span>`;
}

function defaultActionHtml(rowId) {
  const id = escapeAttr(rowId ?? '');
  return `<button class="nlims-action-btn" data-id="${id}" title="View record">&#8226;&#8226;&#8226;</button>`;
}

const COPY_ICON_SVG =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>';

// Renders a single-line value with a copy-to-clipboard button — use for
// Contact-style columns (see ProcessingFacilities.jsx / ServiceProfessionals.jsx).
// Only use inside a column's `render` for the 'display' type; other types
// (filter/sort) should still receive the raw string so search/sort keep working.
export function contactCellHtml(value) {
  const text = String(value ?? '').trim();
  if (!text) return '';
  const attr = escapeAttr(text);
  return `<span class="nlims-contact-cell"><span class="nlims-contact-text">${text}</span><button type="button" class="nlims-copy-btn" data-copy-value="${attr}" title="Copy number" aria-label="Copy number">${COPY_ICON_SVG}</button></span>`;
}

function applyClientFilters(records, filters) {
  if (!filters || Object.keys(filters).length === 0) return records;

  return records.filter((row) =>
    Object.entries(filters).every(([key, val]) => {
      if (val === undefined || val === null || val === '') return true;
      return String(row?.[key] ?? '').toLowerCase() === String(val).toLowerCase();
    })
  );
}

function cleanFilters(filters = {}) {
  const next = {};
  Object.entries(filters).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (typeof value === 'string' && !value.trim()) return;
    next[key] = value;
  });
  return next;
}

function extractCollection(responseData) {
  const payload = responseData?.data ?? responseData ?? {};
  const source =
    Array.isArray(payload)
      ? { items: payload }
      : (Array.isArray(payload.items) || payload.pagination)
        ? payload
        : payload.data ?? payload;

  const items =
    Array.isArray(source?.items)
      ? source.items
      : Array.isArray(source)
        ? source
        : Array.isArray(payload?.items)
          ? payload.items
          : Array.isArray(payload?.results)
            ? payload.results
            : Array.isArray(payload?.data)
              ? payload.data
              : [];

  const pagination = source?.pagination ?? payload?.pagination ?? null;
  const total = Number(pagination?.total ?? items.length);

  return {
    items,
    total: Number.isFinite(total) ? total : items.length,
    pagination,
  };
}

// Column filter popup used only for client-side tables.
function ColFilterPopup({ colIdx, values, rect, initialSelected = [], onApply, onClear, onClose }) {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(initialSelected);

  const displayed = values.filter((value) =>
    String(value).toLowerCase().includes(search.toLowerCase())
  );

  const toggle = (value) =>
    setSelected((prev) =>
      prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value]
    );

  // Opens directly below the clicked filter icon, left edges aligned, and
  // follows it on scroll. Portalled to <body> so a transformed page wrapper
  // can't offset `position: fixed`.
  const { menuRef, style } = useAnchoredMenu(rect, onClose, {
    align: 'left',
    anchorSelector: '.nlims-col-filter-btn',
  });

  return createPortal(
    <div
      ref={menuRef}
      className="nlims-col-filter-popup"
      style={style}
    >
      <div className="flex items-center gap-3 border-b border-gray-100 px-3 py-2">
        <button
          type="button"
          onClick={() => setSelected([...values])}
          className="text-xs font-semibold text-green-700 transition-colors hover:text-green-900"
        >
          Select all ({values.length})
        </button>
        <span className="text-sm text-gray-200">|</span>
        <button
          type="button"
          onClick={() => setSelected([])}
          className="text-xs font-medium text-gray-500 transition-colors hover:text-gray-700"
        >
          Deselect
        </button>
      </div>

      <div className="border-b border-gray-100 px-3 py-2">
        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search..."
          autoFocus
          className="w-full rounded-lg border border-gray-200 px-2.5 py-1.5 text-sm outline-none placeholder-gray-400 focus:border-green-500 focus:ring-1 focus:ring-green-500"
        />
      </div>

      <div className="max-h-44 overflow-y-auto py-1">
        {displayed.length === 0 && (
          <p className="px-3 py-2.5 text-xs italic text-gray-400">No values found</p>
        )}
        {displayed.map((value) => (
          <label
            key={value}
            className="flex cursor-pointer items-center gap-2.5 px-3 py-1.5 hover:bg-green-50"
          >
            <input
              type="checkbox"
              checked={selected.includes(value)}
              onChange={() => toggle(value)}
              className="h-3.5 w-3.5 shrink-0 cursor-pointer rounded accent-green-600"
            />
            <span className="min-w-0 truncate text-sm text-gray-700" title={value}>
              {value}
            </span>
          </label>
        ))}
      </div>

      <div className="flex gap-2 border-t border-gray-100 px-3 py-2.5">
        <button
          type="button"
          onClick={() => onApply(selected)}
          className="flex-1 rounded-md bg-green-600 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-green-700"
        >
          Apply
        </button>
        <button
          type="button"
          onClick={onClear}
          className="flex-1 rounded-md bg-gray-100 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-200"
        >
          Clear
        </button>
      </div>
    </div>,
    document.body
  );
}

function buildDtColumns(columns, enableRowSelection) {
  const checkboxCol = enableRowSelection
    ? [{
        title: '<input type="checkbox" class="nlims-cb-all" aria-label="Select all" />',
        data: null,
        orderable: false,
        className: 'nlims-col-cb dt-body-center dt-head-center',
        width: '36px',
        render: () => '<input type="checkbox" class="nlims-cb-row" aria-label="Select row" />',
      }]
    : [];

  const dataCols = columns.map((col) => {
    const isAction = col.data === null;
    const def = {
      title: col.title,
      data: col.data,
      orderable: isAction ? false : col.sortable !== false,
      className: col.className || '',
      // Pass defaultContent so DataTables doesn't warn on missing row fields
      defaultContent: col.defaultContent !== undefined ? col.defaultContent : '',
    };

    if (col.width) def.width = col.width;

    if (isAction) {
      def.render = col.render || ((_d, _t, row) => defaultActionHtml(row?.id ?? row?.uuid ?? ''));
    } else if (col.render) {
      def.render = col.render;
    } else if (col.data === 'validationStatus') {
      def.render = (data, type) => (type === 'display' ? statusBadgeHtml(data) : (data ?? ''));
    } else if (col.data === 'populationCount' || col.data === 'volume') {
      def.render = (data, type) =>
        type === 'display'
          ? (data != null ? Number(String(data).replace(/,/g, '')).toLocaleString() : '')
          : (data ?? '');
    } else if (col.data === 'estimatedValue') {
      def.render = (data, type) =>
        type === 'display'
          ? (data != null ? `NGN ${Number(data).toLocaleString()}` : '')
          : (data ?? '');
    }

    return def;
  });

  return [...checkboxCol, ...dataCols];
}

export default function NlimsDataTable({
  columns = [],
  data = [],
  ajaxUrl,
  filters = {},
  tableId = 'nlims-table',
  defaultSort,
  serverSide = false,
  enableRowSelection = false,
  onSelectionChange,
  onRowAction,
  onAjaxError,
  pageLength = 10,
  searchQuery,
  hideSearch = false,
  getChildRowHtml = null,
  wrapperClassName = '',
}) {
  // Column set is still controlled by the parent. This layer also applies the
  // approved-only viewer guard so workflow tables never leak draft states.
  const visibleColumns = columns;

  const tableRef = useRef(null);
  const dtRef = useRef(null);
  const filtersRef = useRef(filters);
  const onRowActionRef = useRef(onRowAction);
  const onSelectionRef = useRef(onSelectionChange);
  const onAjaxErrorRef = useRef(onAjaxError);
  const requestSeqRef = useRef(0);
  const abortRef = useRef(null);
  const openColFilterRef = useRef(null);
  // defaultSort via ref: inline array literals (e.g. [0,'asc']) create a new reference on
  // every parent render, which would cause getDefaultOrder to change and the whole DataTable
  // to be destroyed+rebuilt on every state update, wiping all column filters.
  const defaultSortRef = useRef(defaultSort);
  const getChildRowHtmlRef = useRef(getChildRowHtml);

  const [colFilter, setColFilter] = useState(null);
  const [activeColFilters, setActiveColFilters] = useState({});
  const activeColFiltersRef = useRef(activeColFilters);

  useEffect(() => {
    activeColFiltersRef.current = activeColFilters;
  }, [activeColFilters]);

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  useEffect(() => {
    onRowActionRef.current = onRowAction;
  }, [onRowAction]);

  useEffect(() => {
    onSelectionRef.current = onSelectionChange;
  }, [onSelectionChange]);

  useEffect(() => {
    onAjaxErrorRef.current = onAjaxError;
  }, [onAjaxError]);

  useEffect(() => {
    getChildRowHtmlRef.current = getChildRowHtml;
  }, [getChildRowHtml]);

  const useServerSide = Boolean(serverSide && ajaxUrl);

  // Read sort config from ref so changes to the prop's reference don't cause re-initialization
  const getDefaultOrder = useCallback(() => {
    const offset = enableRowSelection ? 1 : 0;
    if (Array.isArray(defaultSortRef.current)) {
      return [[defaultSortRef.current[0] + offset, defaultSortRef.current[1] || 'asc']];
    }
    return [[offset, 'asc']];
  }, [enableRowSelection]);

  useEffect(() => {
    if (!tableRef.current || visibleColumns.length === 0) return;

    const dtColumns = buildDtColumns(visibleColumns, enableRowSelection);
    const order = getDefaultOrder();

    // Set up column-filter opener via ref so native button listeners in initComplete
    // can call it after the DataTable is initialized (dtRef.current resolved at call-time).
    openColFilterRef.current = (dtColIdx, rect) => {
      const dt = dtRef.current;
      if (!dt) return;
      const cbOffset = enableRowSelection ? 1 : 0;
      const userColIdx = dtColIdx - cbOffset;
      const colDef = visibleColumns[userColIdx];
      if (!colDef || colDef.data === null) return;
      const colKey = colDef?.data ?? null;
      const renderFn = colDef?.render ?? null;
      if (useServerSide) {
        const seen = new Set();
        dt.rows({ page: 'current' }).every(function () {
          const rowData = this.data() ?? {};
          const rawVal = colKey != null ? rowData[colKey] : rowData;
          const display = renderFn
            ? String(renderFn(rawVal, 'filter', rowData) ?? '').trim()
            : String(rawVal ?? '').trim();
          if (display) seen.add(display);
        });
        setColFilter((prev) =>
          prev?.colIdx === dtColIdx ? null : { colIdx: dtColIdx, colKey, renderFn, values: [...seen].sort(), rect }
        );
      } else {
        const col = dt.column(dtColIdx);
        const values = col.data().unique().sort().toArray()
          .map((v) => (v != null ? String(v) : null))
          .filter((v) => v !== null && v.trim() !== '');
        setColFilter((prev) =>
          prev?.colIdx === dtColIdx ? null : { colIdx: dtColIdx, colKey, renderFn, values, rect }
        );
      }
    };

    const options = {
      columns: dtColumns,
      order,
      pageLength,
      lengthMenu: useServerSide
        ? [[10, 25, 50, 100], [10, 25, 50, 100]]
        : [[10, 25, 50, 100, -1], [10, 25, 50, 100, 'All']],
      searching: true,
      processing: false,
      serverSide: useServerSide,
      autoWidth: false,
      layout: hideSearch
        ? { topStart: 'pageLength', topEnd: null, bottomStart: 'info', bottomEnd: 'paging' }
        : undefined,
      language: {
        search: '',
        searchPlaceholder: 'Search records...',
        lengthMenu: 'Show _MENU_ entries',
        info: 'Showing _START_ to _END_ of _TOTAL_ records',
        infoEmpty: 'No records available',
        infoFiltered: '(filtered from _MAX_ total)',
        zeroRecords: 'No matching records found',
        emptyTable: 'No data available',
        paginate: { first: '<<', previous: '<', next: '>', last: '>>' },
      },
      initComplete() {
        const api = this.api();
        const cbOffset = enableRowSelection ? 1 : 0;

        api.columns().every(function every(dtColIdx) {
          if (enableRowSelection && dtColIdx === 0) return true;

          const userColIdx = dtColIdx - cbOffset;
          const colDef = visibleColumns[userColIdx];
          if (!colDef || colDef.data === null) return true;

          const th = this.header();
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'nlims-col-filter-btn';
          btn.setAttribute('data-col-idx', String(dtColIdx));
          btn.title = `Filter by ${colDef.title}`;
          btn.innerHTML = '&#9776;';
          // Native listener stops th's DataTables sort-click from stealing this event
          btn.addEventListener('click', (e) => {
            e.stopPropagation();
            e.preventDefault();
            openColFilterRef.current?.(dtColIdx, anchorRect(btn));
          });
          th.appendChild(btn);
        });
      },
    };

    if (useServerSide) {
      options.ajax = async (request, callback) => {
        const draw = Number(request.draw ?? 1);
        const orderInfo = request.order?.[0] ?? {};
        const rawColumnIndex = Number(orderInfo.column ?? 0);
        const userColumnIndex = enableRowSelection ? Math.max(0, rawColumnIndex - 1) : rawColumnIndex;
        const sortBy = visibleColumns[userColumnIndex]?.data ?? '';
        const perPageValue = Number(request.length ?? pageLength);
        const perPage = Number.isFinite(perPageValue) && perPageValue > 0 ? perPageValue : pageLength;
        const start = Number(request.start ?? 0);
        const page = Math.floor(start / perPage) + 1;
        const searchValue = String(request.search?.value ?? '').trim();

        if (abortRef.current) {
          abortRef.current.abort();
        }

        const controller = new AbortController();
        abortRef.current = controller;
        const requestId = ++requestSeqRef.current;

        const params = {
          draw,
          per_page: perPage,
          page,
          search: searchValue,
          q: searchValue,
          sort: sortBy,
          sort_by: sortBy,
          order: orderInfo.dir || 'asc',
          sort_order: orderInfo.dir || 'asc',
          ...cleanFilters(filtersRef.current),
        };

        try {
          const response = await client.get(ajaxUrl, {
            params,
            signal: controller.signal,
          });

          if (requestId !== requestSeqRef.current) return;

          const { items, total } = extractCollection(response.data);
          const visibleItems = filterApprovedViewerRows(items);
          onAjaxErrorRef.current?.('');
          callback({
            draw,
            recordsTotal: total,
            recordsFiltered: total,
            data: visibleItems,
          });
        } catch (error) {
          const wasCanceled =
            controller.signal.aborted ||
            error?.code === 'ERR_CANCELED' ||
            error?.name === 'CanceledError' ||
            error?.message === 'canceled';

          if (wasCanceled) return;

          const message =
            error?.response?.data?.message ||
            error?.response?.data?.error ||
            error?.message ||
            'Unable to load records.';

          onAjaxErrorRef.current?.(message, error);
          callback({
            draw,
            recordsTotal: 0,
            recordsFiltered: 0,
            data: [],
          });
        }
      };
    } else {
      options.data = applyClientFilters(filterApprovedViewerRows(data), filtersRef.current);
    }

    const table = new DataTable(tableRef.current, options);
    dtRef.current = table;
    setActiveColFilters({});

    return () => {
      if (abortRef.current) {
        abortRef.current.abort();
      }
      if (dtRef.current) {
        dtRef.current.destroy();
        dtRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleColumns, useServerSide, ajaxUrl, enableRowSelection, pageLength, getDefaultOrder, hideSearch]);

  // `filters` is usually an inline object (or the `{}` default), so its
  // identity changes on every parent render. Compare by content instead —
  // otherwise any parent re-render (e.g. opening a row's action menu) redraws
  // the table, jumping back to page 1 and moving the rows under the menu.
  const filtersKey = JSON.stringify(cleanFilters(filters ?? {}));
  const drawnFiltersKeyRef = useRef(filtersKey);

  useEffect(() => {
    if (!dtRef.current || useServerSide) return;
    const filtered = applyClientFilters(filterApprovedViewerRows(data), filtersRef.current);
    // New filter values start from page 1; a plain data refresh keeps the
    // current page and scroll position (draw(false)).
    const filtersChanged = drawnFiltersKeyRef.current !== filtersKey;
    drawnFiltersKeyRef.current = filtersKey;
    dtRef.current.clear().rows.add(filtered).draw(filtersChanged);
  }, [data, filtersKey, useServerSide]);

  useEffect(() => {
    filtersRef.current = filters;
    if (!dtRef.current || !useServerSide) return;
    dtRef.current.ajax.reload(null, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey, useServerSide]);

  useEffect(() => {
    if (!dtRef.current || searchQuery === undefined) return;
    dtRef.current.search(searchQuery ?? '').draw();
  }, [searchQuery]);

  useEffect(() => {
    if (!colFilter) return;

    const close = (event) => {
      if (
        !event.target.closest('.nlims-col-filter-popup') &&
        !event.target.closest('.nlims-col-filter-btn')
      ) {
        setColFilter(null);
      }
    };

    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [colFilter]);

  const closeColFilter = useCallback(() => setColFilter(null), []);

  const handleColFilterApply = useCallback((selected) => {
    if (!colFilter || !dtRef.current) return;

    const dt = dtRef.current;

    if (useServerSide) {
      const { colKey, renderFn } = colFilter;
      dt.rows({ page: 'current' }).every(function () {
        const rowData = this.data() ?? {};
        const rawVal = colKey != null ? rowData[colKey] : rowData;
        const display = renderFn
          ? String(renderFn(rawVal, 'filter', rowData) ?? '').trim()
          : String(rawVal ?? '').trim();
        const show = selected.length === 0 || selected.includes(display);
        const node = this.node();
        if (node) node.style.display = show ? '' : 'none';
      });
    } else {
      const col = dt.column(colFilter.colIdx);
      if (selected.length === 0) {
        col.search('').draw();
      } else {
        const pattern = selected
          .map((value) => String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
          .join('|');
        col.search(`^(${pattern})$`, true, false).draw();
      }
    }

    setActiveColFilters((prev) => ({ ...prev, [colFilter.colIdx]: selected }));
    setColFilter(null);
  }, [colFilter, useServerSide]);

  const handleColFilterClear = useCallback(() => {
    if (!colFilter || !dtRef.current) return;

    if (useServerSide) {
      dtRef.current.rows({ page: 'current' }).nodes().each((node) => {
        node.style.display = '';
      });
    } else {
      dtRef.current.column(colFilter.colIdx).search('').draw();
    }

    setActiveColFilters((prev) => {
      const next = { ...prev };
      delete next[colFilter.colIdx];
      return next;
    });
    setColFilter(null);
  }, [colFilter, useServerSide]);

  const handleClick = useCallback((event) => {
    const table = dtRef.current;
    if (!table) return;

    const target = event.target;

    // Filter buttons are handled by native listeners added in initComplete
    if (target.closest('.nlims-col-filter-btn')) return;

    const copyBtn = target.closest('.nlims-copy-btn');
    if (copyBtn) {
      const value = copyBtn.getAttribute('data-copy-value') || '';
      if (value && navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(value).then(() => {
          copyBtn.classList.add('nlims-copy-btn--copied');
          window.clearTimeout(copyBtn._copyResetTimer);
          copyBtn._copyResetTimer = window.setTimeout(() => {
            copyBtn.classList.remove('nlims-copy-btn--copied');
          }, 1200);
        }).catch(() => {});
      }
      return;
    }

    if (target.classList.contains('nlims-cb-all')) {
      const checked = target.checked;
      event.currentTarget
        .querySelectorAll('.nlims-cb-row')
        .forEach((checkbox) => {
          checkbox.checked = checked;
          checkbox.closest('tr')?.classList.toggle('nlims-row-selected', checked);
        });
      onSelectionRef.current?.(checked ? table.rows().data().toArray() : []);
      return;
    }

    if (target.classList.contains('nlims-cb-row')) {
      const tr = target.closest('tr');
      tr?.classList.toggle('nlims-row-selected', target.checked);
      const selected = table.rows('.nlims-row-selected').data().toArray();
      onSelectionRef.current?.(selected);
      return;
    }

    // Child-row action buttons (rendered inside DataTables child rows)
    const childBtn = target.closest('[data-child-action]');
    if (childBtn) {
      const action = childBtn.getAttribute('data-child-action');
      const parentId = childBtn.getAttribute('data-parent-id');
      if (parentId && onRowActionRef.current) {
        let rowData = null;
        table.rows().every(function () {
          const d = this.data();
          if (String(d?.uuid ?? d?.id ?? '') === parentId) {
            rowData = d;
            return false;
          }
        });
        if (rowData) {
          onRowActionRef.current(action, rowData, { button: childBtn, event });
        }
      }
      return;
    }

    const actionBtn = target.closest('.nlims-action-btn');
    if (actionBtn) {
      const action = actionBtn.getAttribute('data-action') || 'view';
      const tr = actionBtn.closest('tr');
      if (!tr) return;

      const dtRow = table.row(tr);
      const rowData = dtRow.data();

      // Inline child-row expansion (DataTables row.child() API)
      if (action === 'expand' && getChildRowHtmlRef.current) {
        if (dtRow.child.isShown()) {
          dtRow.child.hide();
          actionBtn.setAttribute('aria-expanded', 'false');
          actionBtn.innerHTML = 'View More <span class="nlims-action-caret" aria-hidden="true">&#9660;</span>';
        } else {
          const html = getChildRowHtmlRef.current(rowData);
          if (html) {
            dtRow.child(html).show();
            actionBtn.setAttribute('aria-expanded', 'true');
            actionBtn.innerHTML = 'View Less <span class="nlims-action-caret" aria-hidden="true">&#9650;</span>';
          }
        }
        return;
      }

      if (onRowActionRef.current) {
        onRowActionRef.current(action, rowData, { button: actionBtn, row: tr, event });
      }
    }
  }, [useServerSide]);

  return (
    <>
      <div className={`nlims-table-card ${wrapperClassName}`.trim()} onClick={handleClick}>
        <table
          ref={tableRef}
          id={tableId}
          className="nlims-table"
          style={{ width: '100%' }}
        />
      </div>

      {colFilter && (
        <ColFilterPopup
          colIdx={colFilter.colIdx}
          values={colFilter.values}
          rect={colFilter.rect}
          onClose={closeColFilter}
          initialSelected={activeColFilters[colFilter.colIdx] ?? []}
          onApply={handleColFilterApply}
          onClear={handleColFilterClear}
        />
      )}
    </>
  );
}
